import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import SwipeableViews from 'react-swipeable-views'
import { useTheme } from '@mui/material/styles'
import { isEqual } from 'lodash'
import { useVenti } from 'venti'
import { Tabs, Tab, Card, CardContent } from '@mui/material'

import Page from '../../../components/Page'
import { LoadingBtn } from '../../../components/Button'
import { ModalYesNo } from '../../../components/modals/ModalYesNo'
import { ModalError } from '../../../components/modals/ModalError'
import { useAppContext } from '../../../components/AppContext'
import { Loading } from '../../../components/Loading'
import Features from '../../../components/admin/Features'
import { WebsiteSettings } from '../../../components/admin/WebsiteSettings'
import AdminPartnerProfile from './Profile'

import { navigationLinking } from '../../../services/navigation'
import {
	siteConfigurationModelWithNulls,
	validateURL,
} from '../../../services/utils'
import {
	a11yProps,
	isEmailValid,
	getErrorMessage,
	validateSiteConfigurationData,
} from '../../../services/helper'
import { eventTypes } from '../../../services/constants'
import queryKeys from '../../../services/queryKeys'

import { useAlert, useWindowSize } from '../../../hooks'
import { useMixpanel } from '../../../hooks/useMixpanel'
import { getTheme } from '../../../config'
import { TheBigPOSApi } from '../../../lib/TheBigPOSClient'
import MobileSettings from '../../../components/admin/MobileSettings/MobileSettings'

const theme = getTheme()

function TabPanel(props) {
	const { children, value, index, ...other } = props

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`vertical-tabpanel-${index}`}
			aria-labelledby={`vertical-tab-${index}`}
			{...other}
		>
			{value === index && <div className="mt-5">{children}</div>}
		</div>
	)
}

export default function AdminPartnerEdit({ navigation }) {
	const { alert } = useAlert()
	const { id } = useParams()
	const { state } = useAppContext()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { siteConfig } = state
	const ventiState = useVenti()
	const [width] = useWindowSize()
	const themeMUI = useTheme()
	const mixpanel = useMixpanel()

	const isCreateNewPartner = id === 'new'
	const siteConfigurationId = ventiState.get(
		theme.storageKeys.editingPartnerSiteConfigurationId
	)
	const [activeTab, setActiveTab] = useState(0)
	const [modalConfirmation, setModalConfirmation] = useState({
		open: false,
		text: '',
		leavePage: false,
		canBeSaved: false,
		isSave: false,
		event: null,
	})
	const [initialState, setInitialState] = useState({
		...siteConfigurationModelWithNulls,
		entityType: 3,
	})
	const [formData, setFormData] = useState({
		...siteConfigurationModelWithNulls,
		entityType: 3,
		loanOfficerID: '',
		domain: '',
	})
	const [inheritedSiteConfig, setInheritedSiteConfig] = useState({
		...siteConfigurationModelWithNulls,
		entityType: 3,
		loanOfficerID: '',
		domain: '',
	})
	const [canBeSubmitted, setCanBeSubmitted] = useState(true)
	const [password, setPassword] = useState()
	const [errorModalVisible, setErrorModalVisible] = useState(false)
	const [errorMsg, setErrorMsg] = useState('')
	const initialRender = useRef(true)
	const [partner, setPartner] = useState(null)

	const { error: errorFetchingPartner, data: partnerResult } =
		useQuery({
			queryKey: [queryKeys.partner, id],
			queryFn: () => TheBigPOSApi.getPartner(id),
			enabled: !!id && !isCreateNewPartner,
			refetchOnWindowFocus: false,
		})

	const {
		error: errorFetchingPartnerSiteConfiguration,
		data: partnerSiteConfigurationResult,
	} = useQuery({
		queryKey: [
			queryKeys.partnerSiteConfiguration,
			siteConfigurationId,
		],
		queryFn: () =>
			TheBigPOSApi.getPartnerSiteConfiguration(
				id,
				siteConfigurationId
			),
		enabled: !!id && !isCreateNewPartner && !!siteConfigurationId,
		refetchOnWindowFocus: false,
	})

	useEffect(() => {
		if (!id) navigate(`/${navigationLinking.AdminPartner}`)
	}, [id])

	useEffect(() => {
		if (errorFetchingPartner) {
			alert('There was a problem loading the partner', {
				severity: 'error',
			})
		}
	}, [errorFetchingPartner])

	useEffect(() => {
		if (errorFetchingPartnerSiteConfiguration) {
			alert(
				'There was a problem loading the partner site configuration',
				{
					severity: 'error',
				}
			)
		}
	}, [errorFetchingPartnerSiteConfiguration])

	useEffect(() => {
		const { domain, ...rest } = formData
		const canBeSubmitted =
			formData?.url?.length > 0 &&
			isEmailValid(formData?.email?.trim()) &&
			formData?.phone?.replace(/\D/g, '')?.length === 10 &&
			formData?.firstName?.trim()?.length > 0 &&
			formData?.lastName?.trim()?.length > 0 &&
			formData?.title?.trim()?.length > 0 &&
			formData?.branchID?.length > 0 &&
			formData?.loanOfficerID?.length > 0 &&
			!isEqual(initialState, rest)

		setCanBeSubmitted(canBeSubmitted)

		if (initialRender.current) {
			initialRender.current = false
		}
	}, [formData, initialState])

	useEffect(() => {
		if (partnerResult?.data && partnerSiteConfigurationResult?.data) {
			const partnerData = {
				...partnerSiteConfigurationResult.data.siteConfiguration,
				branchID: partnerResult.data.branchID,
				firstName: partnerResult.data.firstName,
				lastName: partnerResult.data.lastName,
				title: partnerResult.data.title,
			}
			setFormData({
				...formData,
				...partnerData,
			})
			setInheritedSiteConfig(
				partnerSiteConfigurationResult.data.inheritedSiteConfiguration
			)
			setInitialState(partnerData)
		}
	}, [partnerResult, partnerSiteConfigurationResult])

	const invalidateQueries = async () => {
		await queryClient.invalidateQueries({
			queryKey: [queryKeys.partners, queryKeys.partner],
		})
	}

	const invalidateSiteConfigurationQueries = async () => {
		await queryClient.invalidateQueries({
			queryKey: [
				queryKeys.partnerSiteConfiguration,
				siteConfigurationId,
			],
		})
	}

	const updatePartnerMutation = useMutation({
		mutationFn: (data) => TheBigPOSApi.replaceUser(data.id, data),
		onSuccess: invalidateQueries,
	})

	const createPartnerMutation = useMutation({
		mutationFn: (data) => TheBigPOSApi.createUser(data),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: [queryKeys.partners],
			})
		},
	})

	const updatePartnerSiteConfigurationMutation = useMutation({
		mutationFn: ({ data, realtorId, siteConfigurationId }) =>
			TheBigPOSApi.replacePartnerSiteConfiguration(
				realtorId,
				siteConfigurationId,
				data
			),
		onSuccess: invalidateSiteConfigurationQueries,
	})

	const createPartnerSiteConfigurationMutation = useMutation({
		mutationFn: ({ data, realtorId }) =>
			TheBigPOSApi.createPartnerSiteConfiguration(realtorId, data),
	})

	const removeUserRelationMutation = useMutation({
		mutationFn: ({ partnerId, relationId }) =>
			TheBigPOSApi.deleteUserRelation(partnerId, relationId),
		onSuccess: invalidateQueries,
	})

	const createUserRelationMutation = useMutation({
		mutationFn: ({ userId1, userId2, relationType }) =>
			TheBigPOSApi.createUserRelation(userId1, {
				userId1,
				userId2,
				relationType,
			}),
		onSuccess: invalidateQueries,
	})

	const createSiteConfiguration = async (partner, loanOfficerID) => {
		if (!partner) return

		await createPartnerSiteConfigurationMutation.mutateAsync({
			data: formData,
			realtorId: partner.id,
		})

		await createUserRelationMutation.mutateAsync({
			userId1: partner.id,
			userId2: loanOfficerID,
			relationType: 'RealtorLoanOfficer',
		})

		const name = `${partner.firstName} ${partner.lastName}`
		mixpanel.trackEvent(eventTypes.PARTNER_CREATED, {
			id: partner.id,
			name,
		})
		alert(`Partner "${name}" successfully created`)
		navigate(`/${navigationLinking.AdminPartner}`)
	}

	const saveForm = async () => {
		try {
			const {
				id: formId,
				name,
				entityID,
				email,
				phone,
				firstName,
				lastName,
				title,
				branchID,
				loanOfficerChanged,
				loanOfficerRelation,
				loanOfficerID,
				url,
				domain,
			} = formData
			// Check the URL is the correct format and matches the domain
			const error = validateURL(url, domain)
			if (error) {
				setErrorMsg(error)
				setErrorModalVisible(true)
				return
			}

			if (formId) {
				validateSiteConfigurationData(formData)
				await updatePartnerMutation.mutateAsync({
					id: entityID,
					email,
					phone: `+1${phone.replace(/\D/g, '')}`,
					firstName,
					lastName,
					title,
					branchId: branchID,
				})
				await updatePartnerSiteConfigurationMutation.mutateAsync({
					data: formData,
					realtorId: entityID,
					siteConfigurationId: formId,
				})
				if (loanOfficerChanged) {
					if (loanOfficerRelation) {
						await removeUserRelationMutation.mutateAsync({
							partnerId: entityID,
							relationId: loanOfficerRelation.id,
						})
					}
					await createUserRelationMutation.mutateAsync({
						userId1: entityID,
						userId2: loanOfficerID,
						relationType: 'RealtorLoanOfficer',
					})
					setFormData((current) => ({
						...current,
						loanOfficerChanged: false,
					}))
				}
				mixpanel.trackEvent(eventTypes.PARTNER_UPDATED, {
					id,
					name,
				})
				alert(`Partner "${name}" successfully updated`)
				navigate(`/${navigationLinking.AdminPartner}`)
			} else {
				validateSiteConfigurationData(formData)
				if (!createPartnerSiteConfigurationMutation.error) {
					const partnerResponse =
						await createPartnerMutation.mutateAsync({
							email,
							phone: `+1${phone.replace(/\D/g, '')}`,
							firstName,
							lastName,
							title,
							branchId: branchID,
							userRole: 'Realtor',
						})

					const partner = partnerResponse?.data
					setPartner(partner)

					await createSiteConfiguration(partner, loanOfficerID)
				} else {
					await createSiteConfiguration(partner, loanOfficerID)
				}
			}
		} catch (e) {
			alert(getErrorMessage(e), { severity: 'error' })
		}
	}

	const handleCheckFormLicenses = () => {
		saveForm()
	}

	const handleTabChange = (event, newValue) => {
		if (newValue !== 0 && !formData.loanOfficerID) {
			setErrorMsg('Please select a loan officer.')
			setErrorModalVisible(true)
			return
		}
		setActiveTab(newValue)
	}

	const handleChangeIndex = (index) => {
		setActiveTab(index)
	}

	const modalConfirm = async (modalConfirmation) => {
		modalConfirmation.leavePage
			? navigation.dispatch(modalConfirmation.event.data.action)
			: setModalConfirmation({
					open: false,
					text: '',
					leavePage: false,
				})
	}

	const handleFeaturesChange = (data) => {
		setFormData((current) => ({
			...current,
			...data,
		}))
	}

	return (
		<Page
			isFullWidth
			title={isCreateNewPartner ? `New Partner` : 'Edit Partner'}
		>
			<ModalYesNo
				modalConfirmation={modalConfirmation}
				modalConfirm={modalConfirm}
				setModalConfirmation={setModalConfirmation}
			/>
			<ModalError
				visible={errorModalVisible}
				setVisible={setErrorModalVisible}
				message={errorMsg}
			/>
			<div className="pl-5 pr-5 pb-20 pt-5 h-screen overflow-auto">
				<p className="text-xl md:text-2xl font-rubik font-bold mr-4 mb-10 dark:text-white">
					{isCreateNewPartner ? `New Partner` : 'Edit Partner'}
				</p>
				{!isCreateNewPartner && !initialState.id ? (
					<div
						className="flex justify-center dark:bg-[#121212]"
						style={{ height: `calc(100vh - 216px)` }}
					>
						<Loading size="small" />
					</div>
				) : (
					<>
						<div className="flex flex-wrap justify-end my-5">
							<LoadingBtn
								id="AdminLoanOfficerEditSaveButton"
								disabled={!canBeSubmitted}
								loading={
									updatePartnerSiteConfigurationMutation.isPending ||
									createPartnerSiteConfigurationMutation.isPending
								}
								text={`${isCreateNewPartner ? 'Create Partner' : 'Update Partner'}`}
								onClick={handleCheckFormLicenses}
								fullWidth={false}
							/>
						</div>
						<Tabs
							variant="scrollable"
							scrollButtons="auto"
							value={activeTab}
							onChange={handleTabChange}
							aria-label="Tabs"
						>
							<Tab label="Partner Profile" {...a11yProps(0)} />
							<Tab label="Branding & Images" {...a11yProps(1)} />
							<Tab label="Features" {...a11yProps(2)} />
							<Tab label="Mobile Settings" {...a11yProps(3)} />
						</Tabs>
						<SwipeableViews
							axis={themeMUI.direction === 'rtl' ? 'x-reverse' : 'x'}
							index={activeTab}
							onChangeIndex={handleChangeIndex}
						>
							<TabPanel
								value={activeTab}
								index={0}
								dir={themeMUI.direction}
							>
								<Card>
									<CardContent>
										<AdminPartnerProfile
											siteConfig={siteConfig}
											formData={formData}
											setFormData={setFormData}
											width={width}
											setPassword={setPassword}
											setInheritedSiteConfiguration={
												setInheritedSiteConfig
											}
											password={password}
										/>
									</CardContent>
								</Card>
							</TabPanel>
							<TabPanel
								value={activeTab}
								index={1}
								dir={themeMUI.direction}
							>
								<Card>
									<CardContent>
										<WebsiteSettings
											formData={formData}
											setFormData={setFormData}
											inheritedSiteConfiguration={inheritedSiteConfig}
											entityType={3}
											width={width}
										/>
									</CardContent>
								</Card>
							</TabPanel>
							<TabPanel
								value={activeTab}
								index={2}
								dir={themeMUI.direction}
							>
								<Card>
									<CardContent>
										<Features
											entityType="Realtor"
											onChange={handleFeaturesChange}
											siteConfiguration={formData}
											inheritedSiteConfiguration={inheritedSiteConfig}
										/>
									</CardContent>
								</Card>
							</TabPanel>
							<TabPanel
								value={activeTab}
								index={3}
								dir={themeMUI.direction}
							>
								<Card>
									<CardContent>
										<MobileSettings entityType="Realtor" />
									</CardContent>
								</Card>
							</TabPanel>
						</SwipeableViews>
					</>
				)}
			</div>
		</Page>
	)
}
